import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ChevronDown, 
  ChevronRight, 
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  Table2, 
  Download,
  BarChart3,
  Columns3,
  Maximize2,
  Upload
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

// Custom tooltip for chart - defined outside component to prevent re-creation on each render
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 shadow-lg">
        <p className="text-[10px] text-gray-400 font-medium">{label}</p>
        <p className="text-xs text-purple-400 font-mono">{payload[0].value.toLocaleString()}</p>
      </div>
    );
  }
  return null;
};

export const AgentResponse = ({ response, agentName, onPublish }) => {
  const [isTableExpanded, setIsTableExpanded] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [chartViewMode, setChartViewMode] = useState('chart'); // 'chart' or 'table' for the chart section
  const [isChartLoading, setIsChartLoading] = useState(true);

  // Simulate chart loading with 5 second delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsChartLoading(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);
  
  if (!response) return null;

  const { introText, highlight, table, chart, insight, downloadLink } = response;

  // Sorting logic for main table
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedRows = () => {
    if (!table || !sortColumn) return table?.rows || [];
    return [...table.rows].sort((a, b) => {
      const aVal = a[sortColumn] || '';
      const bVal = b[sortColumn] || '';
      if (sortDirection === 'asc') {
        return aVal.toString().localeCompare(bVal.toString());
      }
      return bVal.toString().localeCompare(aVal.toString());
    });
  };

  const sortedRows = getSortedRows();
  const totalPages = Math.ceil(sortedRows.length / rowsPerPage);
  const paginatedRows = sortedRows.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="mt-3"
      data-testid="agent-response"
    >
      <div className="rounded-xl bg-gradient-to-b from-slate-900/80 to-slate-950/80 border border-blue-500/50 overflow-hidden shadow-[0_0_6px_rgba(59,130,246,0.2)]">
        {/* Intro Text */}
        {introText && (
          <div className="p-4 pb-3">
            <p className="text-xs text-gray-400 leading-relaxed">{introText}</p>
          </div>
        )}

        {/* Highlight Cards - Horizontal Layout */}
        {highlight && (
          <div className="px-4 pt-2 pb-6">
            <div className="grid grid-cols-2 gap-3">
              {/* Card 1 - Top Customer */}
              <div className="rounded-lg bg-slate-800/40 px-4 py-2">
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3.5 h-3.5 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[9px] text-blue-400/80 font-medium uppercase tracking-wide">Top Customer</p>
                    <p className="text-[11px] font-medium text-white truncate mt-0.5">{highlight.subtitle.split(' leads')[0]}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className="text-sm font-bold text-white font-mono">{highlight.value}</span>
                  </div>
                </div>
              </div>
              
              {/* Card 2 - Total Claims */}
              <div className="rounded-lg bg-slate-800/40 px-4 py-2">
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3.5 h-3.5 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[9px] text-emerald-400/80 font-medium uppercase tracking-wide">Total Claims</p>
                    <p className="text-[11px] font-medium text-white truncate mt-0.5">Top 5 Customers</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className="text-sm font-bold text-white font-mono">12,136</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Data Table */}
        {table && (
          <div className="px-4 pb-4">
            <button
              onClick={() => setIsTableExpanded(!isTableExpanded)}
              className="flex items-center gap-2 text-xs font-medium text-gray-300 hover:text-gray-200 mb-2"
            >
              <Table2 className="w-3.5 h-3.5" />
              <span>{table.title || 'Data Table'}</span>
              {isTableExpanded ? (
                <ChevronDown className="w-3.5 h-3.5" />
              ) : (
                <ChevronRight className="w-3.5 h-3.5" />
              )}
            </button>
            
            {isTableExpanded && (
              <div className="rounded-lg border border-slate-800/50 overflow-hidden bg-slate-900/60">
                {/* Table Toolbar */}
                <div className="flex items-center justify-end gap-1 pl-3 pr-1 py-2 bg-slate-800/30 border-b border-slate-800/50">
                  <button className="p-1.5 hover:bg-slate-700/50 rounded transition-colors text-gray-500 hover:text-gray-300">
                    <Download className="w-3.5 h-3.5" />
                  </button>
                  <button className="flex items-center gap-1 px-2 py-1 hover:bg-slate-700/50 rounded transition-colors text-gray-500 hover:text-gray-300 text-[10px]">
                    <BarChart3 className="w-3.5 h-3.5" />
                    <span>Profile</span>
                    <ChevronDown className="w-3 h-3" />
                  </button>
                  <button className="flex items-center gap-1 px-2 py-1 hover:bg-slate-700/50 rounded transition-colors text-gray-500 hover:text-gray-300 text-[10px]">
                    <Columns3 className="w-3.5 h-3.5" />
                    <span>Columns</span>
                    <ChevronDown className="w-3 h-3" />
                  </button>
                  <button 
                    onClick={() => onPublish && onPublish()}
                    data-testid="publish-btn"
                    className="relative flex items-center gap-1 px-2 py-1 bg-blue-600 hover:bg-blue-500 rounded border-0 transition-colors text-white text-[10px] font-medium overflow-hidden"
                  >
                    <Upload className="w-3 h-3" />
                    Publish
                    <span className="absolute inset-0 shimmer-effect-publish"></span>
                  </button>
                  <button className="p-1.5 hover:bg-slate-700/50 rounded transition-colors text-gray-500 hover:text-gray-300">
                    <Maximize2 className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-800/30">
                        {table.columns.map((col, idx) => (
                          <th 
                            key={idx} 
                            onClick={() => handleSort(col)}
                            className="px-2.5 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-slate-700/30 transition-colors border-b border-slate-800/50"
                          >
                            <div className="flex items-center gap-1">
                              <span>{col}</span>
                              <ChevronDown className={`w-3 h-3 text-gray-600 transition-transform ${sortColumn === col && sortDirection === 'desc' ? 'rotate-180' : ''}`} />
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedRows.map((row, rowIdx) => (
                        <tr 
                          key={rowIdx} 
                          className="border-b border-slate-800/30 last:border-0 hover:bg-slate-800/30 transition-colors"
                        >
                          {table.columns.map((col, colIdx) => (
                            <td 
                              key={colIdx} 
                              className="px-2.5 py-1.5 text-gray-400 font-mono text-[11px]"
                            >
                              {row[col] || row[Object.keys(row)[colIdx]] || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-2.5 py-2 bg-slate-800/30 border-t border-slate-800/50">
                  <div className="flex items-center gap-2 text-[10px] text-gray-500">
                    <span>Rows per page</span>
                    <select 
                      value={rowsPerPage}
                      onChange={(e) => {
                        setRowsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="bg-slate-700/50 border border-slate-600/50 rounded px-1.5 py-0.5 text-gray-400 text-[10px] focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={15}>15</option>
                      <option value={25}>25</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-gray-500">
                      Page {currentPage} of {totalPages || 1}
                    </span>
                    <div className="flex items-center gap-0.5">
                      <button 
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        className="p-1 hover:bg-slate-700/50 rounded transition-colors text-gray-500 hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronsLeft className="w-3.5 h-3.5" />
                      </button>
                      <button 
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="p-1 hover:bg-slate-700/50 rounded transition-colors text-gray-500 hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-3.5 h-3.5" />
                      </button>
                      <button 
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages || totalPages === 0}
                        className="p-1 hover:bg-slate-700/50 rounded transition-colors text-gray-500 hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                      <button 
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages || totalPages === 0}
                        className="p-1 hover:bg-slate-700/50 rounded transition-colors text-gray-500 hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronsRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="px-2.5 py-1.5 bg-slate-800/50 border-t border-slate-800/50 text-center">
                  <span className="text-[10px] text-gray-600 flex items-center justify-center gap-1.5">
                    <Table2 className="w-3 h-3" />
                    Select cells, rows, or a column header to see statistics
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Separator between table and chart */}
        {table && chart && (
          <div className="mx-4 border-t border-slate-800/30"></div>
        )}

        {/* Chart Section with Toggle */}
        {chart && (
          <div className="px-4 py-4">
            {/* Chart Header with Toggle */}
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-medium text-gray-300">{chart.title}</h3>
              <div className="flex bg-slate-800/60 rounded-full p-0.5">
                <button
                  onClick={() => setChartViewMode('chart')}
                  className={`flex items-center gap-1.5 text-[10px] px-3 py-1 rounded-full transition-all ${
                    chartViewMode === 'chart' 
                      ? 'bg-slate-700 text-white' 
                      : 'text-gray-400 hover:text-gray-300'
                  }`}
                >
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path d="M3 9h18M9 21V9" />
                  </svg>
                  Chart
                </button>
                <button
                  onClick={() => setChartViewMode('table')}
                  className={`flex items-center gap-1.5 text-[10px] px-3 py-1 rounded-full transition-all ${
                    chartViewMode === 'table' 
                      ? 'bg-slate-700 text-white' 
                      : 'text-gray-400 hover:text-gray-300'
                  }`}
                >
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path d="M3 9h18M3 15h18M9 3v18M15 3v18" />
                  </svg>
                  Table
                </button>
              </div>
            </div>

            {/* Chart View */}
            {chartViewMode === 'chart' && (
              <div className="group rounded-lg border border-slate-800/50 bg-slate-900/60 p-3">
                {/* Chart toolbar - visible on hover */}
                <div className="flex items-center justify-end gap-0.5 mb-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button className="p-1.5 hover:bg-slate-700/50 rounded transition-colors text-gray-500 hover:text-gray-300">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
                    </svg>
                  </button>
                  <button className="p-1.5 hover:bg-slate-700/50 rounded transition-colors text-gray-500 hover:text-gray-300">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" />
                    </svg>
                  </button>
                  <button className="p-1.5 hover:bg-slate-700/50 rounded transition-colors text-gray-500 hover:text-gray-300">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                    </svg>
                  </button>
                  <button className="p-1.5 hover:bg-slate-700/50 rounded transition-colors text-gray-500 hover:text-gray-300">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 9l-3 3 3 3M9 5l3-3 3 3M15 19l-3 3-3-3M19 9l3 3-3 3" />
                    </svg>
                  </button>
                  <button className="p-1.5 hover:bg-slate-700/50 rounded transition-colors text-gray-500 hover:text-gray-300">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="1" /><circle cx="12" cy="5" r="1" /><circle cx="12" cy="19" r="1" />
                    </svg>
                  </button>
                  <button className="p-1.5 hover:bg-slate-700/50 rounded transition-colors text-gray-500 hover:text-gray-300">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                    </svg>
                  </button>
                  <button className="p-1.5 hover:bg-slate-700/50 rounded transition-colors text-gray-500 hover:text-gray-300">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  </button>
                  <button className="p-1.5 hover:bg-slate-700/50 rounded transition-colors text-gray-500 hover:text-gray-300">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  </button>
                  <button className="p-1.5 hover:bg-slate-700/50 rounded transition-colors text-gray-500 hover:text-gray-300">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                    </svg>
                  </button>
                  <button className="p-1.5 hover:bg-slate-700/50 rounded transition-colors text-gray-500 hover:text-gray-300">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
                    </svg>
                  </button>
                </div>

                {/* Loading State - Option D: Progress Ring */}
                {isChartLoading ? (
                  <div className="h-64 flex flex-col items-center justify-center">
                    <div className="relative w-16 h-16">
                      <svg className="w-16 h-16 transform -rotate-90">
                        <circle
                          cx="32"
                          cy="32"
                          r="28"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                          className="text-slate-700"
                        />
                        <circle
                          cx="32"
                          cy="32"
                          r="28"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                          strokeDasharray="176"
                          strokeDashoffset="44"
                          className="text-blue-500"
                          style={{ 
                            transformOrigin: 'center',
                            animation: 'spin-slow 2s linear infinite'
                          }}
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <BarChart3 className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-3">Generating visualization</p>
                    <style>{`
                      @keyframes spin-slow {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                      }
                    `}</style>
                  </div>
                ) : (
                  /* Bar Chart */
                  <motion.div 
                    className="h-64"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chart.data} margin={{ top: 10, right: 10, bottom: 10, left: -10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                        <XAxis 
                          dataKey="name" 
                          tick={{ fill: '#6b7280', fontSize: 10 }}
                          axisLine={{ stroke: '#334155' }}
                          tickLine={{ stroke: '#334155' }}
                        />
                        <YAxis 
                          tick={{ fill: '#6b7280', fontSize: 10 }}
                          axisLine={{ stroke: '#334155' }}
                          tickLine={{ stroke: '#334155' }}
                          tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toFixed(1)}K` : value}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="value" radius={[3, 3, 0, 0]}>
                          {chart.data.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill="#9333EA" />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </motion.div>
                )}
              </div>
            )}

            {/* Table View (in chart section) */}
            {chartViewMode === 'table' && (
              <div className="rounded-lg border border-slate-800/50 overflow-hidden bg-slate-900/60">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-800/30">
                        <th className="px-2.5 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wide border-b border-slate-800/50">Name</th>
                        <th className="px-2.5 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wide border-b border-slate-800/50">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {chart.data.map((item, idx) => (
                        <tr key={idx} className="border-b border-slate-800/30 last:border-0 hover:bg-slate-800/30 transition-colors">
                          <td className="px-2.5 py-1.5 text-gray-400 font-mono text-[11px]">{item.name}</td>
                          <td className="px-2.5 py-1.5 text-gray-400 font-mono text-[11px]">{item.value.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Insight */}
        {insight && (
          <div className="px-4 pb-4">
            <p className="text-xs text-gray-400 leading-relaxed">{insight}</p>
            
            {/* Download Link */}
            {downloadLink && (
              <a href="#" className="text-xs text-blue-400 hover:text-blue-300 mt-2 inline-block">
                {downloadLink}
              </a>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default AgentResponse;
